
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface Assignment {
  id: string;
  status: 'new' | 'in_progress' | 'completed';
  due_date?: string;
  updates?: string;
  notes?: string;
  scout_task: {
    title: string;
    description?: string;
  };
  athlete_profile: {
    name?: string;
    username?: string;
  };
}

interface EditAssignedTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment | null;
  onSave: (assignmentId: string, updates: Partial<Assignment>) => Promise<void>;
}

const EditAssignedTaskModal: React.FC<EditAssignedTaskModalProps> = ({
  isOpen,
  onClose,
  assignment,
  onSave
}) => {
  const [status, setStatus] = useState<'new' | 'in_progress' | 'completed'>('new');
  const [dueDate, setDueDate] = useState('');
  const [updates, setUpdates] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (assignment) {
      setStatus(assignment.status);
      setDueDate(assignment.due_date || '');
      setUpdates(assignment.updates || '');
    }
  }, [assignment]);

  const handleSave = async () => {
    if (!assignment) {
      toast.error('No assignment selected');
      return;
    }

    try {
      setIsSaving(true);
      
      // Create clean updates object with only changed fields
      const updateData: Partial<Pick<Assignment, 'status' | 'due_date' | 'updates'>> = {
        status,
        due_date: dueDate || undefined,
        updates: updates.trim() || undefined
      };

      console.log('Saving assignment updates:', updateData);
      
      await onSave(assignment.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error saving assigned task:', error);
      toast.error('Failed to update task');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!assignment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Task Assignment</DialogTitle>
          <DialogDescription>
            Update the assignment details for {assignment.athlete_profile.name || assignment.athlete_profile.username}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Task</Label>
            <div className="p-3 bg-muted rounded-md text-sm font-medium">
              {assignment.scout_task.title}
            </div>
          </div>

          {assignment.scout_task.description && (
            <div className="space-y-2">
              <Label>Task Description</Label>
              <div className="p-3 bg-muted rounded-md text-sm">
                {assignment.scout_task.description}
              </div>
            </div>
          )}

          {assignment.notes && (
            <div className="space-y-2">
              <Label>Assignment Notes</Label>
              <div className="p-3 bg-muted rounded-md text-sm">
                {assignment.notes}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="updates">Scout Updates</Label>
            <Textarea
              id="updates"
              value={updates}
              onChange={(e) => setUpdates(e.target.value)}
              placeholder="Add your updates or feedback for this assignment..."
              rows={3}
              disabled={isSaving}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={status} 
                onValueChange={(value: 'new' | 'in_progress' | 'completed') => setStatus(value)} 
                disabled={isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAssignedTaskModal;
